import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { apiRequest, formatCurrency, formatDate } from '../lib/utils';
import { motion } from 'framer-motion';
import { FileText, Home, Car, Building2, Calendar, DollarSign, Shield } from 'lucide-react';

const PoliciesPage = () => {
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const data = await apiRequest('/policies');
        setPolicies(data);
      } catch (error) {
        console.error('Failed to fetch policies:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPolicies();
  }, []);

  const getPolicyIcon = (type) => {
    const icons = {
      home: Home,
      auto: Car,
      business: Building2
    };
    return icons[type] || FileText;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="policies-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Your Policies</h1>
            <p className="text-slate-600 mt-1">Manage and view your insurance coverage</p>
          </div>
        </div>

        {policies.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Shield className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">No Policies Yet</h2>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                You don't have any active policies. Get a quote to start protecting what matters most.
              </p>
              <Button className="bg-slate-900 text-white hover:bg-slate-800" data-testid="get-quote-btn">
                Get Your First Quote
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {policies.map((policy, index) => {
              const Icon = getPolicyIcon(policy.policy_type);
              return (
                <motion.div
                  key={policy.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`policy-card-${policy.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                        <div className="flex items-start gap-4">
                          <div className="h-14 w-14 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Icon className="h-7 w-7 text-blue-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-slate-900 capitalize">
                                {policy.policy_type} Insurance
                              </h3>
                              <span className={`badge ${policy.is_active ? 'badge-success' : 'badge-neutral'}`}>
                                {policy.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <p className="text-sm text-slate-500 mb-2">{policy.policy_number}</p>
                            {policy.property_address && (
                              <p className="text-sm text-slate-600">{policy.property_address}</p>
                            )}
                            {policy.vehicle_info && (
                              <p className="text-sm text-slate-600">{policy.vehicle_info}</p>
                            )}
                            {policy.business_name && (
                              <p className="text-sm text-slate-600">{policy.business_name}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Coverage</p>
                            <p className="font-semibold text-slate-900">
                              {formatCurrency(policy.coverage_amount)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Premium</p>
                            <p className="font-semibold text-slate-900">
                              {formatCurrency(policy.premium)}/yr
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Deductible</p>
                            <p className="font-semibold text-slate-900">
                              {formatCurrency(policy.deductible)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Expires</p>
                            <p className="font-semibold text-slate-900">
                              {formatDate(policy.end_date)}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" data-testid={`view-policy-${policy.id}`}>
                            View Details
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default PoliciesPage;
